import { createReducer } from '@reduxjs/toolkit'
import { cloneDeep } from 'lodash'

import { TSubTaskResponse, TTaskResponse } from 'redux/api/Tasks'

import { arrayToObject } from 'utils/functions'

import * as keyMetricActions from '../../actions/KeyMetrics/actions'
import * as taskActions from '../../actions/Tasks/actions'
import * as authenticationActions from '../../actions/Authentication/actions'
import { EReduxState } from 'redux/utils/State'

export type TasksBody = { [key: string]: TTaskResponse }

export interface TaskReducerType {
  data: TasksBody
  state: EReduxState
}

const INITIAL_STATE: TaskReducerType = {
  data: {},
  state: EReduxState.IDLE
}

function upsertTask(data: TasksBody, task: TTaskResponse | TSubTaskResponse) {
  if (task.parent_task_id === null) {
    data[task.id] = task

    return
  }

  const parent = data[task.parent_task_id]

  if (!parent) {
    return
  }

  const index = parent.sub_tasks.findIndex((subTask) => subTask.id === task.id)

  if (index === -1) {
    parent.sub_tasks.push(task)
  } else {
    parent.sub_tasks[index] = task
  }
}

const taskReducer = createReducer(INITIAL_STATE, (builder) => {
  builder
    // tryGetAllTasks
    .addCase(taskActions.getAll, (state) => {
      state.state = EReduxState.LOADING
    })
    .addCase(taskActions.getAllSuccess, (state, action) => {
      state.data = arrayToObject(action.payload.data)
      state.state = EReduxState.SUCCESS
    })
    .addCase(taskActions.getAllFailed, (state) => {
      state.state = EReduxState.ERROR
    })

    // tryCreateTask
    .addCase(taskActions.createSuccess, (state, action) => {
      upsertTask(state.data, action.payload.data)
    })

    // tryUpdateTask
    .addCase(taskActions.updateSuccess, (state, action) => {
      upsertTask(state.data, action.payload.data)
    })

    // tryDeleteTask
    .addCase(taskActions.destroySuccess, (state, action) => {
      const newData = cloneDeep(state.data)

      delete newData[action.payload]

      for (const taskId in newData) {
        newData[taskId].sub_tasks = newData[taskId].sub_tasks.filter(
          (subTask) => subTask.id !== action.payload
        )
      }

      state.data = newData
    })

    // tryDeleteKeyMetric
    .addCase(keyMetricActions.destroySuccess, (state, action) => {
      const { id } = action.payload

      for (const taskId in state.data) {
        if (state.data[taskId].key_metric_id === id) {
          state.data[taskId].key_metric_id = null
        }
      }
    })

    // Sign out
    .addCase(authenticationActions.signOut, () => INITIAL_STATE)
    .addDefaultCase((state) => state)
})

export default taskReducer
